"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/shared/ui/buttons/button";
import { Input } from "@/shared/ui/inputs/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Textarea } from "@/shared/ui/inputs/textarea";
import { Label } from "@/shared/ui/forms/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { useCreateMatchDispute } from "@/entities/match-dispute";
import type { ApiMatchDTO, ApiTeamDTO } from "@/entities/bracket/api/types";
import { AlertCircle, Plus, Trash2 } from "lucide-react";

export const DISPUTE_REASON_KEYS = [
  "OpponentsLateConnect",
  "LongPause",
  "TournamentRulesViolation",
  "GameRulesViolation",
  "Other",
] as const;

export type DisputeReasonKey = (typeof DISPUTE_REASON_KEYS)[number];

interface CreateDisputeFormProps {
  matchId: string;
  tournamentId: string;
  match: ApiMatchDTO;
  teams: ApiTeamDTO[];
  onSuccess?: () => void;
}

export function CreateDisputeForm({
  matchId,
  tournamentId,
  match,
  teams,
  onSuccess,
}: CreateDisputeFormProps) {
  const t = useTranslations("MatchDispute");
  const [teamId, setTeamId] = useState<string>("");
  const [reasonKey, setReasonKey] = useState<string>("");
  const [comment, setComment] = useState("");
  const [evidenceUrls, setEvidenceUrls] = useState<string[]>([""]);

  const createMutation = useCreateMatchDispute(matchId, tournamentId);

  const teamA = match.teamAId
    ? teams.find((t) => t.id === match.teamAId)
    : null;
  const teamB = match.teamBId
    ? teams.find((t) => t.id === match.teamBId)
    : null;
  const teamOptions = [
    teamA && { id: match.teamAId!, label: teamA.name },
    teamB && { id: match.teamBId!, label: teamB.name },
  ].filter(Boolean) as { id: string; label: string }[];

  const addUrl = () => setEvidenceUrls((prev) => [...prev, ""]);
  const setUrl = (i: number, v: string) =>
    setEvidenceUrls((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  const removeUrl = (i: number) =>
    setEvidenceUrls((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId.trim() || !reasonKey) return;
    const reasonLabel = t(`reasons.${reasonKey}`);
    const reason =
      comment.trim() ? `${reasonLabel}\n\n${comment.trim()}` : reasonLabel;
    const urls = evidenceUrls.filter((u) => u.trim());
    createMutation.mutate(
      { matchId, teamId, reason, evidenceUrls: urls },
      { onSuccess: () => onSuccess?.() }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {t("formTitle")}
        </CardTitle>
        <CardDescription>{t("formDescription")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t("teamLabel")}</Label>
            <Select
              value={teamId}
              onValueChange={setTeamId}
              required
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t("teamPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {teamOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("reasonLabel")}</Label>
            <Select
              value={reasonKey}
              onValueChange={setReasonKey}
              required
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t("reasonPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {DISPUTE_REASON_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`reasons.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("commentLabel")}</Label>
            <Textarea
              className="mt-1 min-h-[100px]"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("commentPlaceholder")}
              maxLength={2000}
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>{t("evidenceLabel")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addUrl}
                className="h-8"
              >
                <Plus className="h-4 w-4 mr-1" /> {t("addEvidence")}
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {evidenceUrls.map((url, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    type="url"
                    placeholder="https://..."
                    value={url}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUrl(i, e.target.value)
                    }
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeUrl(i)}
                    disabled={evidenceUrls.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? t("submitting") : t("submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
